import { useEffect, useMemo, useRef, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import type { Theme } from '@mui/material';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import { Flags } from 'react-feature-flags';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import useKeypress from 'react-use-keypress';
import { v4 as uuidv4 } from 'uuid';

import { useVisitSummaries } from '@/react-query/useVisitSummaries';

import { DataSource } from '../../@types/global';
import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '../../app/appHeader/imageViewerMenu/atoms';
import { isExternalUserAtom } from '../../atoms/atoms';
import { PdfViewerV2 } from '../../common-components/composed/pdfViewerV2/PdfViewerV2';
import {
  INSURANCE_CLAIM_SUMMARY_ERROR_MESSAGE,
  NON_FDA_BUILD,
} from '../../constants';
import {
  generateUserAnnotationRequest,
  useUserAnnotations,
} from '../../react-query/useCreateUserAnnotation';
import {
  generateInsuranceClaimSummaryRequest,
  useGetInsuranceClaimSummary,
} from '../../react-query/useInsuranceClaimSummary';
import { useLatestImagesV2ToV1 } from '../../react-query/useLatestImagesv2';
import { usePatientVisits } from '../../react-query/usePatientVisits';
import { usePreferences } from '../../react-query/usePreferences';
import {
  generateValidationRequest,
  useUpdateUserValidation,
} from '../../react-query/useUpdateUserValidation';
import routes from '../../routes/routes.constants';
import { blueBlack700, white } from '../../theme/videa-style-tokens';
import { useCornerstoneInitialization } from '../../utils/cornerstone/cornerstoneAtoms';
import { isValidToothNumber } from '../../utils/finding';
import {
  useIsEmbedded,
  useRestrictClinicalInteractionsForDirectors,
} from '../../utils/hooks';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import { determineNewPatient } from '../../utils/patient';
import { determineEffectiveSettings } from '../../utils/settings';
import { colors, layout } from '../../utils/theme';
import {
  PracticeFeatureFlagKey,
  useFeatureFlag,
  useIsPatientViewEnabled,
} from '../../utils/user';
import type { PatientListView } from '../patientList/atoms';
import {
  insurancePdfPathAtom,
  isInsurancePdfOpenAtom,
  patientListViewAtom,
  selectQuadrantsAtom,
  selectTeethAtom,
} from '../patientList/atoms';
import { COMPLETED, UPLOADING } from '../uploader/UploaderActions';

import {
  applyImageFilters,
  getPathologiesFromImages,
} from './annotation-utils/annotation-utils';
import AnnotatorConstants from './annotator.constants';
import {
  FmxSortKey,
  imageFilterAtom,
  selectedFmxSortKeyAtom,
  shouldDisplayRootTipsAtom,
  showPastVisitsAtom,
  sidebarIsNotCollapsedAtom,
  viewerModeAtom,
} from './atoms';
import {
  casePresentationModeAtom,
  disableKeyPressAtom,
} from './casePresentation/atoms';
import { CasePresentationHeader } from './casePresentation/CasePresentationHeader';
import { CasePresentationViewer } from './casePresentation/CasePresentationViewer';
import { PreviewDialog } from './casePresentation/patientDownloadable/PreviewDialog/PreviewDialog';
import ConfirmationDialog from './confirmationDialog/ConfirmationDialog';
import { DragAndDropContext } from './dragAndDropContext/DragAndDropContext';
import {
  modelsTogglesAtom,
  toothPartsTogglesAtom,
} from './findingSidebar/atoms';
import { confidenceScoreFilterAtom } from './findingSidebar/confidenceScoreFilter/atoms';
import { useResetConfidenceScoreWhenPatientChanges } from './findingSidebar/confidenceScoreFilter/helpers';
import { shouldUseSeriesView, useCreateInitialFindings } from './helpers';
import {
  enabledImageTypesAtom,
  imageBrowserExpandedAtom,
  imageBrowserOpenAtom,
} from './multiFileViewer/findingViewer/imageBrowser/atoms';
import { useGetFilteredImages } from './multiFileViewer/findingViewer/imageBrowser/helpers';
import {
  showSegmentationAtom,
  showSegmentationFromEmbeddedAtom,
} from './multiFileViewer/imageToolbar/atoms';
import MultiFileViewer from './multiFileViewer/MultiFileViewer';
import { RotationProvider } from './multiFileViewer/RotationContext';
import RblMeasurementGuideDialog from './rblMeasurementGuideDialog/RblMeasurementGuideDialog';
import type {
  CornerstoneFriendlyAnnotation,
  ImageObject,
  ToothIdentificationFinding,
  UserAnnotation,
} from './types';
import {
  ALL_KEYS,
  KEY_MAP,
  computeImagesRefetchInterval,
  getCariesSubTypedFindings,
  useGetModelFeatureFlags,
} from './utils';
import { VisitSidebar } from './visitSidebar/VisitSidebar';

const useStyles = makeStyles<Theme, { embedded: boolean }>({
  container: {
    height: '100vh',
    backgroundColor: colors.PURE_BLACK,
    textAlign: 'left',
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  findingContainer: {
    position: 'relative',
    zIndex: 1,
    padding: '0',
    backgroundColor: 'black',
    height: '100%',
    overflow: 'hidden',
    '@media (max-width: 800px)': {
      height: `100%`,
    },
    '& .odontogram': {
      maxHeight: '26em',
      paddingTop: '6em',
    },
  },
  sidebarContainer: {
    position: 'relative',
    minWidth: layout.FINDINGS_SIDEBAR_WIDTH,
    width: layout.FINDINGS_SIDEBAR_WIDTH,
    height: '100%',
    overflow: 'hidden',
  },
  collapseButton: {
    borderRadius: '1em',
    top: '0.75em',
    left: '0.125em',
    backgroundColor: blueBlack700,
    position: 'absolute',
    cursor: 'pointer',
    '& path': {
      fill: white,
    },
    zIndex: 200,
  },
  sidebarCollapsed: {
    backgroundColor: 'transparent',
    position: 'absolute',
    right: 0,
    minWidth: layout.COLLAPSED_SIDEBAR_WIDTH,
    width: '3em',
    height: '4em',
    justifyContent: 'center',
    display: 'flex',
    '& .MuiSvgIcon-root': {
      left: '0.375em',
      top: '.875em',
    },
    zIndex: 200,
  },
  topBar: {
    height: '3em',
  },
});

export const Annotator = (props: any) => {
  const {
    addAnnotationAction = () => {},
    addMessage = () => {},
    deleteUserAnnotationAction = () => {},
    history = {},
    imageAnalyses = {},
    institution = {},
    practice = {},
    selectedPatientId,
    trackCornerstoneMouseEvent = () => {},
    trackImageInversion,
    trackValidationChange = () => {},
    user = {},
    updateFindingAction = () => {},
    uploaderStatus = '',
    viewPatient = () => {},
    updateToothNumberAction = () => {},
  } = props;

  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;

  const isPlanningModeFlagDisabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.PLANNING_MODE_DISABLED,
  )?.value;

  const currentUploaderStatus = useRef(uploaderStatus);

  // @ts-expect-error TS(2339): Property 'annotator' does not exist on type 'Defau... Remove this comment to see the full error message
  const patients = useSelector((state) => state.annotator?.patients) || {};

  const currentPatient = patients[selectedPatientId] || {};

  const { images } = currentPatient;
  const isEditingRestricted = useRestrictClinicalInteractionsForDirectors();

  const settings = determineEffectiveSettings(institution, practice, user);
  const { autoOpenIncomingPatient } = settings;

  // State
  const [annotationDrawType, setAnnotationDrawType] = useState('caries');
  const [hasHoveredFinding, setHasHoveredFinding] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isInsuranceClaimLoading, setIsInsuranceClaimLoading] = useState(false);
  const [currentlyHoveredImageId, setCurrentlyHoveredImageId] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);

  const [systemFindings, setSystemFindings] = useState([]);
  const [userAnnotations, setUserAnnotations] = useState([]);
  const [agreeEl, setAgreeEl] = useState(null);
  const [watchEl, setWatchEl] = useState(null);
  const [disagreeEl, setDisagreeEl] = useState(null);
  const [isRblMeasurementGuideOpen, setIsRblMeasurementGuideOpen] =
    useState(false);

  // Atoms
  const [viewerMode, setViewerMode] = useAtom(viewerModeAtom);
  const [isInsuranceClaimPdfOpen, setIsInsuranceClaimPdfOpen] = useAtom(
    isInsurancePdfOpenAtom,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'generatedInsuranceClaimPdfLink' is assigned a value but never used.
  const [generatedInsuranceClaimPdfLink, setGeneratedInsuranceClaimPdfLink] =
    useAtom(insurancePdfPathAtom);

  const [showSegmentation, setShowSegmentation] = useAtom(showSegmentationAtom);
  const [showSegmentationFromEmbedded] = useAtom(
    showSegmentationFromEmbeddedAtom,
  );
  const [toothPartsToggles, setToothPartsToggles] = useAtom(
    toothPartsTogglesAtom,
  );
  const confidenceScore = useAtomValue(confidenceScoreFilterAtom);

  const [selectedTeeth, setSelectedTeeth] = useAtom(selectTeethAtom);
  const [selectedQuadrants, setSelectedQuadrants] =
    useAtom(selectQuadrantsAtom);
  const setImageBrowserExpanded = useSetAtom(imageBrowserExpandedAtom);
  const setImageBrowserOpen = useSetAtom(imageBrowserOpenAtom);
  const shouldDisableKeyPress = useAtomValue(disableKeyPressAtom);
  const [isExternalUser, setExternalUser] = useAtom(isExternalUserAtom);
  const selectedVisit = useAtomValue(selectedVisitAtom);
  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const setEnabledImageTypes = useSetAtom(enabledImageTypesAtom);
  const [selectedUnifiedId, setSelectedUnifiedId] = useAtom(
    selectedUnifiedIdAtom,
  );
  const [currentImageFilter, setCurrentImageFilter] = useAtom(imageFilterAtom);
  const [isExpandedSidebar, setIsExpandedSidebar] = useAtom(
    sidebarIsNotCollapsedAtom,
  );
  const [enabledFindingTypes, setEnabledFindingTypes] =
    useAtom(modelsTogglesAtom);
  const [shouldDisplayRootTips, setShouldDisplayRootTips] = useAtom(
    shouldDisplayRootTipsAtom,
  );
  const [showPastVisits, setShowPastVisits] = useAtom(showPastVisitsAtom);
  const selectedHuddleTab = useAtomValue(patientListViewAtom);

  // Hooks
  // Need to reset confidence scores at the annotator level when changing patients
  useResetConfidenceScoreWhenPatientChanges();
  const getInsuranceClaimSummaryMutation = useGetInsuranceClaimSummary();
  const isSegmentationEnabled = useIsPatientViewEnabled();
  const isEmbedded = useIsEmbedded();
  useCornerstoneInitialization();

  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();

  const classes = useStyles({ embedded: isEmbedded });

  const { data: preferences, isLoading: isPreferencesLoading } =
    usePreferences();

  const { trackEvent } = useTrackEvent();
  const { data: patientVisitsData } = usePatientVisits({
    patientId: currentPatient?.patientId,
  });

  const { t } = useLingui();

  const isPlanningModeDisabled =
    preferences?.isPlanningModeDisabled || isPlanningModeFlagDisabled;

  const toggleIsInsuranceClaimPdfOpen = () => {
    if (!isInsuranceClaimPdfOpen) {
      setIsInsuranceClaimLoading(true);

      getInsuranceClaimSummaryMutation.mutate(
        generateInsuranceClaimSummaryRequest(
          currentPatient?.patientId,
          currentPatient?.name,
          Object.keys(images || {}),
          user.primaryPracticeId,
          confidenceScore,
        ),
        {
          onError: () => {
            addMessage(INSURANCE_CLAIM_SUMMARY_ERROR_MESSAGE, 'error', 2000);
            setIsInsuranceClaimLoading(false);
          },
          onSuccess: (data) => {
            setGeneratedInsuranceClaimPdfLink(
              data?.insuranceClaimSummaryPdfPath,
            );
            setIsInsuranceClaimPdfOpen(true);
            setIsInsuranceClaimLoading(false);
          },
          onSettled: () => {
            const estimatedVisitId =
              // @ts-expect-error TS(2571): Object is of type 'unknown'.
              Object.values(images)?.at(0)?.estimatedVisitId;
            trackEvent(mixpanelEvents.insurancePdf, {
              patientId: currentPatient.patientId,
              estimatedVisitId,
            });
          },
        },
      );
    } else {
      setIsInsuranceClaimPdfOpen(false);
    }
  };

  const handleExpandSidebar = (isNotCollapsed: any) => {
    setIsExpandedSidebar(isNotCollapsed);
  };

  const { createInitialFindings } = useCreateInitialFindings();

  // Temporarily have both standalone and embedded default at 30s, though keeping separate variables as they will likely diverge again soon

  // Guardrails, allow a minimum of 1s and a maximum of 10min for the polling interval
  const refetchInterval: number | undefined = computeImagesRefetchInterval(
    preferences,
    isEmbedded,
    isCasePresentationMode || showPastVisits,
  );

  const latestImages = useLatestImagesV2ToV1({
    videaPatientId: selectedPatientId,
    externalPatientId: currentPatient?.externalPatientId,
    options: {
      enabled: currentPatient?.externalPatientId
        ? currentPatient.dataSource === DataSource.NUCLEUS
        : true,
      onSuccess: (data) => createInitialFindings(data, selectedPatientId),
      refetchInterval,
    },
    returnAllFmxSorts: true,
  });
  const fmxIndexesByType = latestImages.data?.fmxIndexesByType;
  const [selectedFmxSortKey, setSelectedFmxSortKey] = useAtom(
    selectedFmxSortKeyAtom,
  );
  const selectedFmxIndexes = fmxIndexesByType?.find(
    (type) => type.key === selectedFmxSortKey,
  )?.fmxIndexes;

  const imageBrowserLength = useGetFilteredImages({
    disableImageFilters: true,
  })?.length;

  const imageBrowserLengthNoToothSelection = useGetFilteredImages({
    disableImageFilters: true,
    disableSelectTeeth: true,
  })?.length;

  // Only show up to 18 images, since that is a full-mouth x-ray
  const imagesArray: ImageObject[] = useMemo(() => {
    return currentPatient?.images ? Object.values(currentPatient.images) : [];
  }, [currentPatient?.images]);

  const { imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const activeImage: ImageObject | undefined = imagesArray?.find(
    (image) => image?.objectId === selectedImageId,
  );

  // Hack to hide sidebar for dental photos
  const isSidebarNotCollapsed =
    activeImage?.detectedImageType === 'COLOR_IMAGE'
      ? false
      : isExpandedSidebar;

  // Determine default image filter based on Series view logic
  const defaultImageFilter = useMemo(() => {
    if (!imagesArray?.length) return undefined;
    return shouldUseSeriesView(imagesArray, preferences?.enableSeriesView)
      ? AnnotatorConstants.BITEWING
      : AnnotatorConstants.FMX;
  }, [imagesArray, preferences?.enableSeriesView]);

  // Check if Series view is currently selected (≤4 FMX images AND user hasn't manually selected FMX)
  const isSeriesViewSelected =
    shouldUseSeriesView(imagesArray, preferences?.enableSeriesView) &&
    currentImageFilter === AnnotatorConstants.BITEWING;

  // For Series view, override fmxIndex with bwViewIndex for proper positioning
  const imagesArrayWithSeriesOverride = useMemo(
    () =>
      isSeriesViewSelected
        ? imagesArray.map((image) => {
            if (
              image &&
              image.bwViewIndex !== null &&
              image.bwViewIndex !== undefined
            ) {
              return {
                ...image,
                fmxIndex: image.bwViewIndex,
              };
            }
            return image;
          })
        : imagesArray,
    [imagesArray, isSeriesViewSelected],
  );

  const sortedByFMXIndex = useMemo(() => {
    // When no FMX sort type is selected, use image.fmxIndex directly
    if (!selectedFmxIndexes) {
      return [...Array(18)].map((_, index) => {
        const image = imagesArrayWithSeriesOverride.find(
          ({ fmxIndex }) => fmxIndex === index,
        );
        return image || { fmxIndex: index };
      });
    }

    return [...Array(18)].map((_, index) => {
      const slot = selectedFmxIndexes.find(
        ({ fmxIndex }) => fmxIndex === index,
      );

      if (slot?.imageIds?.length) {
        const targetImageId = slot.imageIds[0];
        const image = imagesArrayWithSeriesOverride.find(
          (img) =>
            (img.analysisId && String(img.analysisId) === targetImageId) ||
            (img.imageManifestId &&
              String(img.imageManifestId) === targetImageId),
        );
        if (image) return image;
      }

      return { fmxIndex: index };
    });
  }, [imagesArrayWithSeriesOverride, selectedFmxIndexes]);

  // Default to bitewing view when Series view conditions are met on first load
  useEffect(() => {
    if (currentImageFilter || !imagesArray.length || !preferences) {
      return;
    }

    if (shouldUseSeriesView(imagesArray, preferences?.enableSeriesView)) {
      setCurrentImageFilter(AnnotatorConstants.BITEWING);
      return;
    }
    setCurrentImageFilter(AnnotatorConstants.FMX);
  }, [imagesArray, setCurrentImageFilter, currentImageFilter, preferences]);

  const sortedAndFilteredImages: ImageObject[] | undefined = useMemo(() => {
    if (currentImageFilter && isValidToothNumber(currentImageFilter)) {
      const imageData = imagesArray.filter((image) => {
        const isToothFound = image.models?.tooth_identification?.findings.some(
          (toothIds: ToothIdentificationFinding) =>
            toothIds.toothNumber.toString() === currentImageFilter,
        );
        return isToothFound && image;
      });
      return applyImageFilters(
        imageData,
        currentImageFilter,
        selectedTeeth,
        selectedQuadrants,
      );
    }

    if (
      selectedTeeth.length > 0 &&
      currentImageFilter === AnnotatorConstants.ODONTOGRAM
    ) {
      // Sort images by fmxIndex, with undefined/null values at the end
      const sortedImagesArray = [...imagesArray].sort((a, b) => {
        const aIndex = a.fmxIndex;
        const bIndex = b.fmxIndex;

        return (aIndex ?? 999) - (bIndex ?? 999);
      });

      return applyImageFilters(
        sortedImagesArray,
        currentImageFilter,
        selectedTeeth,
        selectedQuadrants,
      );
    }

    return applyImageFilters(
      sortedByFMXIndex,
      currentImageFilter,
      selectedTeeth,
      selectedQuadrants,
    );
  }, [
    imagesArray,
    currentImageFilter,
    sortedByFMXIndex,
    selectedTeeth,
    selectedQuadrants,
  ]);

  const imageKeysToDisplay = (sortedAndFilteredImages || [])
    ?.map((image: any) => image.objectId)
    .filter((imageId: any) => !!imageId);

  const numberOfSortedFMXImages = useMemo(
    () =>
      sortedByFMXIndex.filter(
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        (image) => !!image.objectId,
      ).length,
    [sortedByFMXIndex],
  );

  const numberOfBitewings = useMemo(
    () =>
      sortedByFMXIndex.filter(
        (image) =>
          // @ts-expect-error TS(2571): Object is of type 'unknown'.
          !!image.imageId &&
          // @ts-expect-error TS(2571): Object is of type 'unknown'.
          AnnotatorConstants.BW_ROW_INDEXES.includes(image.fmxIndex),
      ).length,
    [sortedByFMXIndex],
  );

  // For Series view, count all images that would be displayed
  const numberOfSeriesImages = useMemo(() => {
    if (!shouldUseSeriesView(imagesArray, preferences?.enableSeriesView))
      return numberOfBitewings;

    return imagesArrayWithSeriesOverride.filter(
      (image) =>
        !!image.imageId &&
        image.fmxIndex !== null &&
        image.fmxIndex !== undefined,
    ).length;
  }, [
    imagesArray,
    preferences?.enableSeriesView,
    numberOfBitewings,
    imagesArrayWithSeriesOverride,
  ]);

  const [{ data: visitSummaries, isLoading: isVisitSummariesLoading }] =
    useVisitSummaries(
      {
        visits: [
          {
            estimatedVisitId: selectedVisit
              ? parseInt(selectedVisit)
              : sortedAndFilteredImages?.[0]?.estimatedVisitId,
            unifiedAppointmentId: selectedUnifiedId
              ? parseInt(selectedUnifiedId)
              : undefined,
          },
        ],
      },
      undefined,
      { refetchInterval },
    );
  const visitSummary = visitSummaries?.[0];

  // Need unifiedId populated for note generation to work
  useEffect(() => {
    if (
      visitSummary &&
      !selectedUnifiedId &&
      visitSummary.unifiedAppointmentId
    ) {
      setSelectedUnifiedId(visitSummary.unifiedAppointmentId.toString());
    }
  }, [visitSummary, selectedUnifiedId, setSelectedUnifiedId]);

  const userValidationMutation = useUpdateUserValidation();

  const updateFinding = (
    finding: any,
    newValidationValue: any,
    newValidationReason: any,
    newValidationReasons: any,
  ) => {
    if (activeImage?.models) {
      trackValidationChange(
        user,
        selectedPatientId,
        activeImage,
        finding,
        newValidationValue,
        newValidationReason,
        newValidationReasons,
      );
      if (isSalesDemo) {
        return updateFindingAction(
          selectedPatientId,
          activeImage,
          finding,
          newValidationValue,
        );
      }

      const { imageId } = activeImage;

      const userValidationData = generateValidationRequest(
        finding,
        preferences?.enableLatestImagesV2 && activeImage.analysisId
          ? String(activeImage.analysisId)
          : imageId,
        newValidationValue,
      );
      userValidationMutation.mutate(userValidationData, {
        onError: () =>
          addMessage(
            t`Sorry, we could not save your changes. Please try again later`,
            'error',
            2000,
          ),
        onSettled: () => {
          updateFindingAction(
            selectedPatientId,
            activeImage,
            finding,
            newValidationValue,
          );
        },
      });
    }
  };

  const updateToothNumber = (
    oldToothNumber: any,
    newToothNumber: any,
    finding: any,
    editedViaFindingViewer: any,
  ) => {
    if (activeImage && activeImage.findings) {
      updateToothNumberAction(
        selectedPatientId,
        activeImage,
        user,
        imageAnalyses,
        finding,
        newToothNumber,
        oldToothNumber,
        editedViaFindingViewer,
      );
    }
  };

  /**
   * changeViewerMode() is fired after annotations or drawn, or one of the action icons are selected
   */
  const changeViewerMode = (newMode: any) => {
    if (
      viewerMode === AnnotatorConstants.MODES.VIEWING &&
      !enabledFindingTypes.user
    ) {
      // This should only happen if the icon in the left nav was selected to enable viewing mode. Display the user annotations if they are toggled off
      // Otherwise, the user may get confused if they draw an annotation and it immediately disappears
      setEnabledFindingTypes({
        ...enabledFindingTypes,
        user: true,
      });
    }

    setViewerMode(newMode);
  };

  useKeypress(ALL_KEYS, (event: any) => {
    // if most annotations are enabled, the 'all' toggle should turn all off
    // if most annotations are disabled, the 'all' toggle should turn all on
    let count = 0;
    Object.keys(enabledFindingTypes).forEach((type) => {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      if (enabledFindingTypes[type]) count++;
    });
    const findingTypesCount = Object.keys(enabledFindingTypes).length;
    const isOverallAnnotationState = count > findingTypesCount / 2;

    if (shouldDisableKeyPress) {
      return;
    }

    switch (true) {
      case KEY_MAP.keyPressedAllAnnotations.includes(event.key): {
        const newFindingsTypes = {
          ...enabledFindingTypes,
          periapical_radiolucency: isPrlEnabled && !isOverallAnnotationState,
          radiographic_boneloss: isRblEnabled && !isOverallAnnotationState,
          incipients: !isOverallAnnotationState,
          calculus: isCalculusEnabled && !isOverallAnnotationState,
          caries: !isOverallAnnotationState,
          area_of_concern: isCariesSubclassEnabled && !isOverallAnnotationState,
          restorative_adjacent_lucency:
            isVdaEnabled && !isOverallAnnotationState,
        };
        setToothPartsToggles({
          tooth_anatomy: !isOverallAnnotationState,
        });
        setEnabledFindingTypes(newFindingsTypes);
        trackEvent(mixpanelEvents.toggleFindings, {
          toggled: {
            category: 'all_findings',
            value: !isOverallAnnotationState,
          },
          previousFindingsTypes: enabledFindingTypes,
          newFindingsTypes: newFindingsTypes,
          patientId: selectedPatientId,
          method: 'hotkey_press',
        });
        break;
      }

      case KEY_MAP.keyPressedPatientView.includes(event.key):
        if (isSegmentationEnabled && !isCasePresentationMode) {
          setShowSegmentation(!showSegmentation);
        }
        break;

      case KEY_MAP.keyPressedToothParts.includes(event.key):
        if (isSegmentationEnabled) {
          setToothPartsToggles({
            tooth_anatomy: !toothPartsToggles.tooth_anatomy,
          });
        }
        break;

      case KEY_MAP.keyPressedPerioFindings.includes(event.key): {
        const newFindingsTypes = {
          ...enabledFindingTypes,
          radiographic_boneloss:
            isRblEnabled && !enabledFindingTypes.radiographic_boneloss,
          calculus: isCalculusEnabled && !enabledFindingTypes.calculus,
        };
        setEnabledFindingTypes(newFindingsTypes);
        trackEvent(mixpanelEvents.toggleFindings, {
          toggled: {
            category: 'periodontal',
            value: isRblEnabled && !enabledFindingTypes.radiographic_boneloss,
          },
          previousFindingsTypes: enabledFindingTypes,
          newFindingsTypes: newFindingsTypes,
          patientId: selectedPatientId,
          method: 'hotkey_press',
        });
        break;
      }

      case KEY_MAP.keyPressedRestoFindings.includes(event.key): {
        const newFindingsTypes = {
          ...enabledFindingTypes,
          caries: !enabledFindingTypes.caries,
          incipients: !enabledFindingTypes.caries,
          area_of_concern:
            isCariesSubclassEnabled && !enabledFindingTypes.area_of_concern,
          restorative_adjacent_lucency:
            isVdaEnabled && !enabledFindingTypes.restorative_adjacent_lucency,
        };
        setEnabledFindingTypes(newFindingsTypes);
        trackEvent(mixpanelEvents.toggleFindings, {
          toggled: {
            category: 'restorative',
            value: !enabledFindingTypes.caries,
          },
          previousFindingsTypes: enabledFindingTypes,
          newFindingsTypes: newFindingsTypes,
          patientId: selectedPatientId,
          method: 'hotkey_press',
        });
        break;
      }
      case KEY_MAP.keyPressedMagnify.includes(event.key) &&
        !isCasePresentationMode:
        changeViewerMode(
          viewerMode === AnnotatorConstants.MODES.MAGNIFYING
            ? AnnotatorConstants.MODES.VIEWING
            : AnnotatorConstants.MODES.MAGNIFYING,
        );
        break;

      case KEY_MAP.keyPressedEndoFindings.includes(event.key): {
        const newFindingsTypes = {
          ...enabledFindingTypes,
          periapical_radiolucency:
            isPrlEnabled && !enabledFindingTypes.periapical_radiolucency,
        };
        setEnabledFindingTypes(newFindingsTypes);
        trackEvent(mixpanelEvents.toggleFindings, {
          toggled: {
            category: 'endodontic',
            value: isPrlEnabled && !enabledFindingTypes.periapical_radiolucency,
          },
          previousFindingsTypes: enabledFindingTypes,
          newFindingsTypes: newFindingsTypes,
          patientId: selectedPatientId,
          method: 'hotkey_press',
        });
        break;
      }
    }
  });

  const { appendUserAnnotation, removeUserAnnotation } = useUserAnnotations();

  const addAnnotation = (
    image: any,
    newAnnotation: CornerstoneFriendlyAnnotation,
  ) => {
    const { imageManifestId } = image;
    trackEvent(mixpanelEvents.userFinding, {
      patientId: selectedPatientId,
      annotation: newAnnotation,
      // TODO: is it safe to change this?
      imageId: imageManifestId,
    });
    if (isSalesDemo || isEditingRestricted) {
      const findingId = uuidv4();
      addAnnotationAction(selectedPatientId, image, newAnnotation, findingId);
    } else {
      const userAnnotationRequest = generateUserAnnotationRequest(
        newAnnotation,
        imageManifestId,
      );
      appendUserAnnotation.mutate(userAnnotationRequest, {
        onError: () =>
          addMessage(
            t`Sorry, we could not save your changes. Please try again later`,
            'error',
            2000,
          ),
        onSettled: (annotationId) => {
          const findingId = annotationId ?? uuidv4();
          // Wrap addAnnotation so that we can add the state property, imageAnalyses, rather than passing that prop to the grandchild that actually uses it
          addAnnotationAction(
            selectedPatientId,
            image,
            newAnnotation,
            findingId,
          );
        },
      });
    }
  };

  const deleteUserAnnotation = (
    image: ImageObject,
    finding: UserAnnotation | null,
  ) => {
    const { imageManifestId } = image ?? {};
    if (!imageManifestId || !finding) {
      addMessage(
        t`Sorry, we could not save your changes. Please try again later`,
        'error',
        2000,
      );
      return;
    }

    removeUserAnnotation.mutate(
      {
        imageManifestId: imageManifestId,
        annotationId: finding.annotationId,
      },
      {
        onSettled: () => {
          deleteUserAnnotationAction(
            selectedPatientId,
            finding,
            images,
            imageAnalyses,
          );
        },
        onError: () => {
          addMessage(
            t`Sorry, we could not save your changes. Please try again later`,
            'error',
            2000,
          );
        },
      },
    );
  };

  const clearActiveImage = () => {
    setViewerMode(AnnotatorConstants.MODES.VIEWING);
    // Keep the current filter if it's a valid view (BW or FMX), otherwise use default
    if (
      currentImageFilter === AnnotatorConstants.BITEWING ||
      currentImageFilter === AnnotatorConstants.FMX
    ) {
      // Keep current filter
    } else {
      setCurrentImageFilter(defaultImageFilter);
    }
    const newRoute = `${
      isEmbedded ? '/embedded' : ''
    }/annotator/${selectedPatientId}`;
    history.push({ pathname: newRoute, search: history.location.search });
    setSelectedTeeth([]);
    setSelectedQuadrants([]);
    setImageBrowserExpanded(false);
    setEnabledImageTypes([
      'dentalPhotos',
      'fmxPhotos',
      'panoPhotos',
      'fmxAsOther',
    ]);
  };

  const anchorEls = {
    agree: agreeEl,
    watch: watchEl,
    disagree: disagreeEl,
  };

  const handleUpdateOdontogramView = (isVisible: any) => {
    const newOdontogramView = isVisible
      ? AnnotatorConstants.ODONTOGRAM
      : currentImageFilter;
    setCurrentImageFilter(newOdontogramView);
    if (isVisible) {
      trackEvent(mixpanelEvents.odontogramView, {
        patientId: currentPatient.patientId,
      });
    }
  };

  const returnToUpload = () => {
    history.push(routes.UPLOADER);
    // Just in case let's clear jotai storage here for external links
    sessionStorage.removeItem('jotai-practicePatientId');
    sessionStorage.removeItem('jotai-practiceImageId');
  };

  // Effects
  useEffect(() => {
    if (isFirstLoad) {
      if (
        currentImageFilter === AnnotatorConstants.ODONTOGRAM &&
        selectedTeeth.length
      ) {
        return;
      }
      setCurrentImageFilter(defaultImageFilter);
    }
  }, [
    defaultImageFilter,
    setCurrentImageFilter,
    isFirstLoad,
    currentImageFilter,
    selectedTeeth,
  ]);

  useEffect(() => {
    setIsExpandedSidebar(!isEmbedded);
  }, [setIsExpandedSidebar, isEmbedded]);

  useEffect(() => {
    setSelectedQuadrants([]);
    setEnabledImageTypes([
      'dentalPhotos',
      'fmxPhotos',
      'panoPhotos',
      'fmxAsOther',
    ]);
  }, [setSelectedQuadrants, setEnabledImageTypes]);

  useEffect(() => {
    setShouldDisplayRootTips(true);
    // Default to false on any new patient
    if (isPreferencesLoading) {
      return;
    }
    if (!showSegmentationFromEmbedded) {
      setShowSegmentation(preferences?.showSegmentationView ?? false);
      if (isEmbedded) {
        trackEvent(mixpanelEvents.embeddedPatientView, { patientView: true });
      }
    } else {
      setShowSegmentation(true);
      trackEvent(mixpanelEvents.embeddedPatientView, { patientView: true });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'setShowSegmentation'. Either include it or remove the dependency array. */
  }, [isPreferencesLoading, selectedPatientId]);

  // Init enabled finding types
  useEffect(() => {
    setEnabledFindingTypes({
      caries: true,
      incipients: true,
      user: true,
      periapical_radiolucency: isPrlEnabled,
      radiographic_boneloss: isRblEnabled,
      calculus: isCalculusEnabled,
      area_of_concern: isCariesSubclassEnabled,
      restorative_adjacent_lucency: isVdaEnabled,
    });
  }, [
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isPrlEnabled,
    isRblEnabled,
    isVdaEnabled,
    setEnabledFindingTypes,
  ]);

  useEffect(() => {
    if (!currentPatient.patientId || !patientVisitsData || !selectedVisit) {
      return;
    }
    let planningReviewMode: null | PatientListView | 'EXTERNAL' =
      selectedHuddleTab;
    if (isPlanningModeDisabled) {
      planningReviewMode = null;
    }
    if (isExternalUser) {
      planningReviewMode = 'EXTERNAL';
    }
    // After tracking this event we can set it back to false as the bridge has already been taken
    setExternalUser(false);

    trackEvent(mixpanelEvents.viewPatient, {
      patientId: currentPatient.patientId,
      planningReviewMode: planningReviewMode,
      estimatedVisitDate: patientVisitsData.visits.find(
        (visit) => visit.id?.toString() === selectedVisit,
      )?.visitDate,
    });
    // We do not want to trigger this event multiple times for the same instance of Annotator
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'isExternalUser', 'isPlanningModeDisabled', 'selectedHuddleTab', 'setExternalUser', and 'trackEvent'. Either include them or remove the dependency array. */
  }, [currentPatient.patientId, selectedVisit, patientVisitsData]);

  // Whenever we leave the current patient, reset showPastVisits and selectedTeeth
  useEffect(() => {
    return () => {
      setShowPastVisits(false);
      setSelectedTeeth([]);
      setSelectedQuadrants([]);
      setImageBrowserExpanded(false);
      setSelectedFmxSortKey(FmxSortKey.CURRENT_ORDER);
    };
  }, [
    setShowPastVisits,
    setSelectedTeeth,
    setImageBrowserExpanded,
    setSelectedQuadrants,
    setSelectedFmxSortKey,
  ]);

  // Reset selectedFmxSortKey when patient changes
  useEffect(() => {
    setSelectedFmxSortKey(FmxSortKey.CURRENT_ORDER);
  }, [selectedPatientId, setSelectedFmxSortKey]);

  useEffect(() => {
    if (selectedTeeth.length && isFirstLoad && !latestImages.isLoading) {
      const hasSelectedTeethImages = !!latestImages.data?.analyses?.some(
        (analysis) =>
          analysis.teeth?.some((tooth) =>
            selectedTeeth.includes(tooth.toothId),
          ),
      );
      setIsFirstLoad(false);
      setImageBrowserExpanded(hasSelectedTeethImages);
      setImageBrowserOpen(
        hasSelectedTeethImages ? true : !!imageBrowserLengthNoToothSelection,
      );
      if (!hasSelectedTeethImages) {
        setSelectedTeeth([]);
      }
    } else if (isFirstLoad && !latestImages.isLoading) {
      setIsFirstLoad(false);
      setImageBrowserOpen(!!imageBrowserLengthNoToothSelection);
    }
  }, [
    isFirstLoad,
    latestImages.isLoading,
    imageBrowserLength,
    imageBrowserLengthNoToothSelection,
    setImageBrowserOpen,
    selectedTeeth.length,
    setImageBrowserExpanded,
    setSelectedTeeth,
    latestImages.data?.analyses,
    selectedTeeth,
  ]);

  useEffect(() => {
    if (
      autoOpenIncomingPatient &&
      currentUploaderStatus.current !== uploaderStatus &&
      uploaderStatus === UPLOADING
    ) {
      addMessage('New images detected - uploading...');
    } else if (
      autoOpenIncomingPatient &&
      currentUploaderStatus.current !== uploaderStatus &&
      uploaderStatus === COMPLETED
    ) {
      const newPatient = determineNewPatient(Object.values(patients));

      if (newPatient && newPatient.patientId) {
        const { firstName, lastName, patientId = '' } = newPatient;
        const nameToDisplay =
          firstName && lastName ? `${firstName} ${lastName}` : 'latest patient';

        history.push({
          pathname: `${routes.ANNOTATOR}/${patientId}`,
          search: history.location.search,
        });
        viewPatient(patientId);
        addMessage(
          `Analysis complete - now viewing ${nameToDisplay}`,
          'success',
        );
      }
    }

    currentUploaderStatus.current = uploaderStatus;
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'addMessage', 'autoOpenIncomingPatient', 'history', and 'viewPatient'. Either include them or remove the dependency array. */
  }, [patients, user.settings, uploaderStatus]);

  useEffect(() => {
    if (
      (numberOfSortedFMXImages === 0 && numberOfBitewings === 0) ||
      latestImages.isLoading
    ) {
      return;
    }

    setCurrentImageFilter((current) => {
      if (currentImageFilter === AnnotatorConstants.OTHER) {
        return AnnotatorConstants.OTHER;
      }
      if (
        (currentImageFilter && isValidToothNumber(currentImageFilter)) ||
        (currentImageFilter === AnnotatorConstants.ODONTOGRAM &&
          selectedTeeth.length)
      ) {
        return currentImageFilter;
      }

      // Only apply Series view if user hasn't explicitly chosen FMX
      if (
        shouldUseSeriesView(imagesArray, preferences?.enableSeriesView) &&
        currentImageFilter !== AnnotatorConstants.FMX
      ) {
        return AnnotatorConstants.BITEWING;
      }

      if (numberOfBitewings === 0 && numberOfSortedFMXImages !== 0) {
        return AnnotatorConstants.FMX;
      }
      if (numberOfBitewings !== 0 && numberOfSortedFMXImages === 0) {
        return AnnotatorConstants.BITEWING;
      }

      return current;
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'currentImageFilter' and 'setCurrentImageFilter'. Either include them or remove the dependency array. */
  }, [latestImages.isLoading, numberOfBitewings, numberOfSortedFMXImages]);

  /**
   * Determine the list of relevant systemFindings and annotations based upon whether there is an active (selected) image
   * If there is, show only its systemFindings and annotations in the sidebar. Otherwise, show them all
   */
  useEffect(() => {
    const {
      cariesFindings = [],
      userAnnotations: existingUserAnnotations = [],
      rblFindings = [],
      prlFindings = [],
      calculusFindings = [],
      restorativeAdjacentLucencyFindings = [],
    } = getPathologiesFromImages(activeImage, images, imageKeysToDisplay);
    let cariesSystemFindings = [];
    let areaOfConcernFindings: any = [];

    if (isCariesSubclassEnabled) {
      const { caries, areasOfConcern } =
        getCariesSubTypedFindings(cariesFindings);
      cariesSystemFindings = caries;
      areaOfConcernFindings = areasOfConcern;
    } else {
      cariesSystemFindings = cariesFindings;
    }

    const allSystemFindings = [
      ...cariesSystemFindings,
      ...prlFindings,
      ...rblFindings,
      ...calculusFindings,
      ...areaOfConcernFindings,
      ...restorativeAdjacentLucencyFindings,
    ];
    // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
    setSystemFindings(allSystemFindings);
    setUserAnnotations(existingUserAnnotations);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'imageKeysToDisplay'. Either include it or remove the dependency array. */
  }, [images, activeImage, currentImageFilter, isCariesSubclassEnabled]);
  return (
    <>
      <PreviewDialog />
      <RotationProvider selectedPatientId={selectedPatientId}>
        {!isCasePresentationMode ? (
          <Grid container className={classes.container}>
            <DragAndDropContext imagesArray={imagesArray}>
              <Grid item className={classes.findingContainer} xs>
                <MultiFileViewer
                  // @ts-expect-error TS(2740): Type '{}' is missing the following properties from... Remove this comment to see the full error message
                  activeImage={activeImage}
                  isUsingLatestModel={latestImages.data?.usingLatestModel}
                  addAnnotation={addAnnotation}
                  deleteUserAnnotation={deleteUserAnnotation}
                  annotationDrawType={annotationDrawType}
                  annotatorAnchorEls={anchorEls}
                  changeViewerMode={changeViewerMode}
                  currentImageFilter={currentImageFilter}
                  currentlyHoveredImageId={currentlyHoveredImageId}
                  currentPatient={currentPatient}
                  enabledFindingTypes={enabledFindingTypes}
                  onExpandSidebar={handleExpandSidebar}
                  onUpdateOdontogramView={handleUpdateOdontogramView}
                  hasHoveredFinding={hasHoveredFinding}
                  imageKeysToDisplay={imageKeysToDisplay}
                  isFetchingLatestImages={latestImages.isLoading}
                  isSalesDemo={isSalesDemo}
                  numberOfBitewings={numberOfSeriesImages}
                  numberOfSortedFMXImages={numberOfSortedFMXImages}
                  onChangeViewerMode={changeViewerMode}
                  onLeaveImage={clearActiveImage}
                  patientVisitsData={patientVisitsData}
                  shouldUseSeriesView={shouldUseSeriesView(
                    imagesArray,
                    preferences?.enableSeriesView,
                  )}
                  onSetCurrentImageFilter={setCurrentImageFilter}
                  onSetCurrentImageIndex={setCurrentImageIndex}
                  onSetCurrentlyHoveredImageId={setCurrentlyHoveredImageId}
                  onSetEnabledFindingTypes={setEnabledFindingTypes}
                  shouldDisplayRootTips={shouldDisplayRootTips}
                  sidebar={{}}
                  sidebarIsNotCollapsed={isSidebarNotCollapsed}
                  sortedAndFilteredImages={sortedAndFilteredImages}
                  trackCornerstoneMouseEvent={trackCornerstoneMouseEvent}
                  trackImageInversion={trackImageInversion}
                  updateFinding={updateFinding}
                  updateToothNumber={updateToothNumber}
                  user={user}
                  viewerMode={viewerMode}
                  visitSummary={visitSummary}
                  fmxIndexesByType={fmxIndexesByType}
                />
              </Grid>
            </DragAndDropContext>

            {!showPastVisits && (
              <Flags authorizedFlags={[NON_FDA_BUILD]}>
                <VisitSidebar
                  activeImage={activeImage}
                  dob={latestImages.data?.dob}
                  isExpandedSidebar={isSidebarNotCollapsed}
                  isInsuranceClaimLoading={isInsuranceClaimLoading}
                  isLoading={isVisitSummariesLoading}
                  onOpenInsuranceClaimPdf={toggleIsInsuranceClaimPdfOpen}
                  patientName={latestImages.data?.patientName}
                  setIsExpandedSidebar={setIsExpandedSidebar}
                  sortedAndFilteredImages={sortedAndFilteredImages}
                  visitSummary={visitSummary}
                />
              </Flags>
            )}
            <ConfirmationDialog
              onOk={returnToUpload}
              isOpen={isConfirmationOpen}
              setIsOpen={setIsConfirmationOpen}
            />
            <RblMeasurementGuideDialog
              isOpen={isRblMeasurementGuideOpen}
              setIsOpen={setIsRblMeasurementGuideOpen}
            />
            <PdfViewerV2 />
          </Grid>
        ) : (
          <Grid container className={classes.container}>
            <CasePresentationHeader />
            <CasePresentationViewer
              activeImage={activeImage}
              viewerMode={viewerMode}
              imagesArray={imagesArray}
            />
          </Grid>
        )}
      </RotationProvider>
    </>
  );
};

Annotator.propTypes = {
  addAnnotationAction: PropTypes.func,
  addMessage: PropTypes.func,
  deleteUserAnnotationAction: PropTypes.func,
  history: PropTypes.object,
  imageAnalyses: PropTypes.object,
  institution: PropTypes.object,
  practice: PropTypes.object,
  selectedPatientId: PropTypes.string,
  trackCornerstoneMouseEvent: PropTypes.func,
  trackImageInversion: PropTypes.func,
  trackValidationChange: PropTypes.func,
  updateFindingAction: PropTypes.func,
  uploaderStatus: PropTypes.string,
  user: PropTypes.object,
  viewPatient: PropTypes.func,
  updateToothNumberAction: PropTypes.func,
  addImageToState: PropTypes.func,
};
